import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowRight, ArrowUpRight, ClipboardCheck, ClipboardList, HardDrive, ShieldCheck } from "lucide-react";
import { AndamentoModulo, ModuloKey, RiepilogoModuli as RiepilogoData } from "../../api/dashboard";
import { useAuth } from "../../context/AuthContext";
import { SECTION_COLORS, SectionKey } from "../../utils/sectionColors";
import { useCountUp } from "../../hooks/useCountUp";
import { Sparkline } from "./Sparkline";

/**
 * `permission` è quella di LETTURA del modulo, la stessa su cui filtrano il
 * menu (NAV_ITEMS) e la route guard: senza, la card portava a una pagina che
 * la guard rimanda indietro. Per l'Istituto di Vigilanza era il caso di
 * "Installazioni" — una tessera ferma a 0 che non apriva niente.
 */
const MODULI: {
  key: ModuloKey;
  label: string;
  to: string;
  icon: typeof HardDrive;
  section: SectionKey;
  permission: string;
}[] = [
  { key: "installazioni", label: "Installazioni", to: "/schedule", icon: HardDrive, section: "installazioni", permission: "installazioni:read" },
  { key: "sopralluoghi", label: "Sopralluoghi", to: "/sopralluoghi", icon: ClipboardCheck, section: "sopralluoghi", permission: "sopralluoghi:read" },
  { key: "interventi", label: "Interventi", to: "/interventions", icon: ClipboardList, section: "interventi", permission: "interventi:read" },
  { key: "attivazioni", label: "Attivazioni", to: "/activations", icon: ShieldCheck, section: "attivazioni", permission: "attivazioni:read" },
];

function Trend({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-ink/40">
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        invariato
      </span>
    );
  }
  const su = delta > 0;
  const Icon = su ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${su ? "text-[#15803d]" : "text-[#dc2626]"}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {su ? "+" : ""}
      {delta} questo mese
    </span>
  );
}

function ModuloCard({
  label,
  to,
  icon: Icon,
  section,
  dati,
  isLoading,
}: {
  label: string;
  to: string;
  icon: typeof HardDrive;
  section: SectionKey;
  dati: AndamentoModulo | undefined;
  isLoading: boolean;
}) {
  const colore = SECTION_COLORS[section].base;
  const animato = useCountUp(dati?.totale ?? 0);

  return (
    <Link
      to={to}
      // Barra verticale colorata sul bordo sinistro: identifica il modulo a
      // colpo d'occhio, con lo stesso colore del pulsante di azione rapida.
      style={{ borderLeftColor: colore }}
      className="flex min-w-0 flex-col rounded-card border border-line border-l-4 bg-white p-4 shadow-card transition-[box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:shadow-lift"
    >
      {/* Riga superiore: icona + nome a sinistra, sparkline a destra. La
          sparkline è volutamente piccola e senza assi né griglia — indica
          l'andamento a colpo d'occhio, non è un grafico da leggere. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${colore}1a`, color: colore }}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="truncate text-sm font-semibold text-ink">{label}</span>
        </div>
        {!isLoading && dati && (
          <div className="h-6 w-16 shrink-0 sm:w-20">
            <Sparkline serie={dati.serie} color={colore} className="h-full w-full" />
          </div>
        )}
      </div>

      <p className="mt-3 text-3xl font-bold tabular-nums text-ink">{isLoading ? "—" : animato}</p>

      <div className="mt-1 min-h-[18px]">{!isLoading && dati && <Trend delta={dati.delta} />}</div>

      {/* Record fuori dalla serie storica: dichiarati invece che nascosti, così
          il totale resta spiegabile (es. installazioni già programmate). */}
      {!isLoading && dati && (dati.futuri > 0 || dati.senza_data > 0) && (
        <p className="mt-2 truncate text-[11px] text-ink/40">
          {[dati.futuri > 0 ? `${dati.futuri} in programma` : null, dati.senza_data > 0 ? `${dati.senza_data} senza data` : null]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}
    </Link>
  );
}

/**
 * "Riepilogo Moduli": una card per ciascuno dei quattro moduli con totale,
 * trend rispetto al mese precedente e andamento a sparkline. I dati arrivano
 * da GET /api/dashboard/riepilogo, già scopizzato lato server: ogni ruolo vede
 * i propri numeri senza che il frontend debba filtrare nulla.
 */
export function RiepilogoModuli({ dati, isLoading }: { dati: RiepilogoData | null; isLoading: boolean }) {
  const { hasPermission } = useAuth();
  const moduli = MODULI.filter((m) => hasPermission(m.permission));
  // Nessun modulo leggibile: la sezione sparisce invece di lasciare un titolo
  // sopra una griglia vuota.
  if (moduli.length === 0) return null;

  return (
    <section className="mb-8">
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Riepilogo moduli</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {moduli.map((m) => (
          <ModuloCard
            key={m.key}
            label={m.label}
            to={m.to}
            icon={m.icon}
            section={m.section}
            dati={dati?.[m.key]}
            isLoading={isLoading}
          />
        ))}
      </div>
    </section>
  );
}
